import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, X, Link2, CalendarClock, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const STATUS_CLS = {
  requested: "bg-gold/15 text-gold",
  payment_link_sent: "bg-violet/10 text-violet",
  scheduled: "bg-blue-500/10 text-blue-600",
  paid: "bg-emerald-500/10 text-emerald-600",
  cancelled: "bg-navy/10 text-navy/40",
};
const inputCls = "w-full bg-cream/60 border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";

export default function ShopOrders() {
  const { t } = useI18n();
  const [orders, setOrders] = useState(null);
  const [active, setActive] = useState(null);
  const [link, setLink] = useState({ payment_link: "", shipping_cost: "", message: "" });
  const [sched, setSched] = useState({ message: "", payment_link: "", shipping_cost: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const openManage = (o) => {
    setActive(o);
    setLink({ payment_link: o.payment_link || "", shipping_cost: o.shipping_cost ?? "", message: "" });
    setSched({ message: "", payment_link: o.payment_link || "", shipping_cost: o.shipping_cost ?? "" });
  };

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
      setActive(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const sendLink = () => act(async () => {
    if (!link.payment_link.trim()) { toast.error(t("os.shopOrders.manage.linkRequired")); throw new Error("link"); }
    await api.post(`/shop/orders/${active.id}/send-link`, {
      payment_link: link.payment_link.trim(),
      shipping_cost: parseFloat(link.shipping_cost || 0),
      message: link.message,
    });
    toast.success(t("os.shopOrders.manage.linkSent"));
  });
  const sendSchedule = () => act(async () => {
    if (!sched.message.trim()) { toast.error(t("os.shopOrders.manage.dateRequired")); throw new Error("msg"); }
    await api.post(`/shop/orders/${active.id}/schedule`, {
      message: sched.message.trim(),
      payment_link: sched.payment_link.trim(),
      shipping_cost: parseFloat(sched.shipping_cost || 0),
    });
    toast.success(t("os.shopOrders.manage.dateSent"));
  });
  const markPaid = () => act(async () => { await api.post(`/shop/orders/${active.id}/mark-paid`); toast.success(t("os.shopOrders.manage.markedPaid")); });
  const cancel = () => act(async () => { await api.post(`/shop/orders/${active.id}/cancel`); toast.success(t("os.shopOrders.manage.cancelled")); });

  const kpis = (() => {
    const list = orders || [];
    const paid = list.filter((o) => o.status === "paid");
    return {
      total: list.length,
      paid: paid.length,
      pending: list.filter((o) => o.status === "requested").length,
      revenue: paid.reduce((s, o) => s + Number(o.total || o.subtotal || 0), 0),
    };
  })();

  const st = (s) => t(`os.shopOrders.status.${s}`) || s;

  return (
    <OSLayout title={t("os.nav.shopOrders")}>
      {orders === null ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">AURA · Commerce</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.nav.shopOrders")}</h2>
            <p className="mt-2 text-navy/50 max-w-xl">{t("os.shopOrders.subtitle")}</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["total", String(kpis.total)], ["pending", String(kpis.pending)], ["paid", String(kpis.paid)], ["revenue", money(kpis.revenue)]].map(([key, val], i) => (
              <motion.div key={key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`shoporders-kpi-${key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{t(`os.shopOrders.kpi.${key}`)}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{val}</div>
              </motion.div>
            ))}
          </div>

          {orders.length === 0 ? (
            <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg" data-testid="shoporders-empty">{t("os.shopOrders.empty")}</div>
          ) : (
            <div className="bg-white border border-navy/10 rounded-lg overflow-hidden overflow-x-auto" data-testid="shoporders-table">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="border-b border-navy/10 text-navy/40 text-left">
                    {["order", "customer", "items", "total", "status", "vetta", "actions"].map((c) => (
                      <th key={c} className="px-5 py-3 font-medium overline text-[0.6rem]">{t(`os.shopOrders.cols.${c}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`shoporder-row-${o.id}`} className="border-b border-navy/5 last:border-0 hover:bg-cream/50 transition-colors">
                      <td className="px-5 py-4 font-mono text-xs">#{(o.id || "").slice(0, 8).toUpperCase()}</td>
                      <td className="px-5 py-4">
                        <div className="font-medium">{o.customer?.name || "—"}</div>
                        <div className="text-xs text-navy/40">{o.customer?.email}</div>
                      </td>
                      <td className="px-5 py-4 text-navy/60 max-w-[200px] truncate">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                      <td className="px-5 py-4 font-medium">{money(o.total || o.subtotal)} <span className="text-navy/30 text-xs">MXN</span></td>
                      <td className="px-5 py-4"><span className={`text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_CLS[o.status] || "bg-navy/10 text-navy/50"}`}>{st(o.status)}</span></td>
                      <td className="px-5 py-4">
                        {o.vetta_code ? (
                          <Link to="/os/vetta" className="inline-flex items-center gap-1 text-violet hover:underline font-mono text-xs">{o.vetta_code} <ExternalLink className="w-3 h-3" /></Link>
                        ) : <span className="text-navy/30">{t("os.shopOrders.notLinked")}</span>}
                      </td>
                      <td className="px-5 py-4">
                        <button data-testid={`shoporder-manage-${o.id}`} onClick={() => openManage(o)}
                          className="text-xs px-3 py-1.5 rounded-full border border-navy/15 hover:border-violet hover:text-violet transition-colors">
                          {t("os.shopOrders.manage.btn")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Manage modal */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => !busy && setActive(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()} data-testid="shoporder-manage-modal"
            className="bg-cream rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-navy/10">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-cream">
              <div>
                <div className="overline text-navy/40 text-[0.6rem]">{t("os.shopOrders.manage.title")}</div>
                <div className="font-mono text-sm">#{active.id.slice(0, 8).toUpperCase()}</div>
              </div>
              <button onClick={() => setActive(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Summary */}
              <div className="bg-white rounded-lg border border-navy/10 p-4">
                <div className="text-sm font-medium">{active.customer?.name}</div>
                <div className="text-xs text-navy/40">{active.customer?.email} · {active.customer?.phone || "—"}</div>
                <div className="text-xs text-navy/50 mt-1">{active.customer?.address} {active.customer?.city} {active.customer?.postal_code}</div>
                <div className="mt-3 space-y-1 text-sm">
                  {(active.items || []).map((i, idx) => (
                    <div key={idx} className="flex justify-between"><span>{i.name} ×{i.qty}</span><span className="text-navy/50">{money(i.price * i.qty)}</span></div>
                  ))}
                </div>
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-navy/10"><span className="text-navy/50">{t("shop.subtotal")}</span><span className="font-medium">{money(active.subtotal)} MXN</span></div>
                <div className="mt-2"><span className={`text-[11px] px-2.5 py-1 rounded-full ${STATUS_CLS[active.status]}`}>{st(active.status)}</span></div>
              </div>

              {/* Send payment link */}
              <div className="bg-white rounded-lg border border-navy/10 p-4">
                <div className="flex items-center gap-2 font-medium text-sm mb-3"><Link2 className="w-4 h-4 text-violet" /> {t("os.shopOrders.manage.sendLinkTitle")}</div>
                <input data-testid="manage-payment-link" placeholder={t("os.shopOrders.manage.linkPlaceholder")} value={link.payment_link} onChange={(e) => setLink({ ...link, payment_link: e.target.value })} className={inputCls} />
                <input data-testid="manage-shipping" type="number" placeholder={t("os.shopOrders.manage.shippingPlaceholder")} value={link.shipping_cost} onChange={(e) => setLink({ ...link, shipping_cost: e.target.value })} className={`${inputCls} mt-2`} />
                <input placeholder={t("os.shopOrders.manage.msgPlaceholder")} value={link.message} onChange={(e) => setLink({ ...link, message: e.target.value })} className={`${inputCls} mt-2`} />
                {link.shipping_cost !== "" && (
                  <div className="flex justify-between text-sm mt-2 text-navy/60">
                    <span>{t("os.shopOrders.manage.totalWithShip")}</span>
                    <span className="font-medium text-navy">{money(Number(active.subtotal || 0) + Number(link.shipping_cost || 0))} MXN</span>
                  </div>
                )}
                <button data-testid="manage-send-link" disabled={busy} onClick={sendLink} className="mt-3 w-full rounded-full bg-gradient-violet text-white py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-60">
                  {t("os.shopOrders.manage.sendLinkBtn")}
                </button>
              </div>

              {/* Schedule date + payment */}
              <div className="bg-white rounded-lg border border-navy/10 p-4">
                <div className="flex items-center gap-2 font-medium text-sm mb-1"><CalendarClock className="w-4 h-4 text-blue-600" /> {t("os.shopOrders.manage.scheduleTitle")}</div>
                <p className="text-xs text-navy/50 mb-3">{t("os.shopOrders.manage.scheduleHint")}</p>
                <textarea data-testid="manage-date-msg" rows={3} placeholder={t("os.shopOrders.manage.datePlaceholder")} value={sched.message} onChange={(e) => setSched({ ...sched, message: e.target.value })} className={inputCls} />
                <input data-testid="manage-sched-link" placeholder={t("os.shopOrders.manage.linkPlaceholder")} value={sched.payment_link} onChange={(e) => setSched({ ...sched, payment_link: e.target.value })} className={`${inputCls} mt-2`} />
                <input data-testid="manage-sched-shipping" type="number" placeholder={t("os.shopOrders.manage.shippingPlaceholder")} value={sched.shipping_cost} onChange={(e) => setSched({ ...sched, shipping_cost: e.target.value })} className={`${inputCls} mt-2`} />
                {sched.payment_link && sched.shipping_cost !== "" && (
                  <div className="flex justify-between text-sm mt-2 text-navy/60">
                    <span>{t("os.shopOrders.manage.totalWithShip")}</span>
                    <span className="font-medium text-navy">{money(Number(active.subtotal || 0) + Number(sched.shipping_cost || 0))} MXN</span>
                  </div>
                )}
                <button data-testid="manage-send-date" disabled={busy} onClick={sendSchedule} className="mt-3 w-full rounded-full bg-navy text-white py-2.5 text-sm hover:bg-navy-soft transition-colors disabled:opacity-60">
                  {t("os.shopOrders.manage.scheduleBtn")}
                </button>
              </div>

              {/* Final actions */}
              <div className="flex gap-3">
                <button data-testid="manage-mark-paid" disabled={busy} onClick={markPaid} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/40 text-emerald-600 py-2.5 text-sm hover:bg-emerald-500/5 transition-colors disabled:opacity-60">
                  <CheckCircle2 className="w-4 h-4" /> {t("os.shopOrders.manage.markPaidBtn")}
                </button>
                <button data-testid="manage-cancel" disabled={busy} onClick={cancel} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 text-navy/50 py-2.5 text-sm hover:border-destructive hover:text-destructive transition-colors disabled:opacity-60">
                  <Ban className="w-4 h-4" /> {t("os.shopOrders.manage.cancelBtn")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </OSLayout>
  );
}
